import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Grid,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import { capitalize, uniq } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import {
  getPetsAllReportData,
  getPetsReportData,
} from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import {
  bloodGroups,
  genderList,
  NeuteredList,
  petOwnershipTypesForDrop,
  reportPetTypes,
  temperaments,
} from "../../../../util/dropList";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";
import "./PetOverviewReport.css";

const tableHdrs = [
  "petName",
  "petType",
  "ownershipType",
  "petStatus",
  "species",
  "gender",
  "breed",
  "color",
  "reproductiveStatus",
  "bloodGroup",
  "temperament",
  "lastVisitDate",
];
const filterByList = [
  "Gender",
  "Breed",
  "Color",
  "Reproductive Status",
  "Blood Group",
  "Temperament",
  "Last Visit Date",
];
const dropFields = [
  "Gender",
  "Breed",
  "Reproductive Status",
  "Blood Group",
  "Temperament",
];
const initSearch = {
  petName: "",
  petType: [],
  ownershipType: [],
  petStatus: [],
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};

const PetDetailsReport = () => {
  const dispatch = useDispatch();
  const [dateRange, setDateRange] = useState("2025-08 to 2026-08");
  const [selectedPetType, setSelectedPetType] = useState("all");
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const { pets, allPets } = useSelector((state) => state?.report);
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(getPetsAllReportData());
  }, []);

  useEffect(() => {
    onApiCall(1);
  }, [
    dateRange,
    selectedPetType,
    searchValues?.petName,
    searchValues?.petType,
    searchValues?.ownershipType,
    searchValues?.petStatus,
  ]);

  const overviewData = pets?.overview?.data || pets?.overview || {};

  // 1. Cards Parsing
  const cardsObj = overviewData?.cards || {};

  const activeCount =
    cardsObj?.activePets ??
    overviewData?.activePets?.activePets ??
    overviewData?.activePets?.count ??
    "150";

  const inactiveCount =
    cardsObj?.inactivePets ??
    overviewData?.inactivePets?.count ??
    "101";

  const newPetsCount =
    cardsObj?.newPets ??
    overviewData?.newPets?.count ??
    "0";

  const reactivationRateVal =
    cardsObj?.reactivationRate ??
    overviewData?.reactivationRate ??
    "79.0";

  const reactivationRateStr =
    typeof reactivationRateVal === "number" || !String(reactivationRateVal).includes("%")
      ? `${reactivationRateVal}%`
      : reactivationRateVal;

  const strayNgoObj = cardsObj?.strayNgoIntake || overviewData?.strayNgoIntake || {};
  const strayVal =
    strayNgoObj?.display ??
    (strayNgoObj?.count !== undefined && strayNgoObj?.total !== undefined
      ? `${strayNgoObj.count} of ${strayNgoObj.total} (${strayNgoObj.percentage}%)`
      : "3 of 64 (4.7%)");

  const summaryCards = [
    { title: "Active pets", value: `${activeCount}` },
    { title: "Inactive pets", value: `${inactiveCount}` },
    { title: "New pets", value: `${newPetsCount}` },
    { title: "Reactivation rate", value: `${reactivationRateStr}` },
    { title: "Stray/NGO intake", value: `${strayVal}` },
  ];

  // 2. Total Pets Trend Line Chart Parsing
  const trendArr = Array.isArray(overviewData?.totalPetsTrend)
    ? overviewData.totalPetsTrend
    : Array.isArray(overviewData?.yearlyTrends)
    ? overviewData.yearlyTrends
    : null;

  let trendCategories = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  let thisYearData = [2, 5, 65, 40, 8, 6, 7, 6, 0, 0, 0, 0];
  let lastYearData = [2, 4, 18, 40, 25, 12, 9, 12, 8, 6, 7, 6];

  if (trendArr && trendArr.length > 0) {
    trendCategories = trendArr.map((item) => String(item.month || item.name || ""));
    thisYearData = trendArr.map((item) => Number(item.thisYear ?? item.currentYear ?? 0));
    lastYearData = trendArr.map((item) => Number(item.lastYear ?? item.previousYear ?? 0));
  }

  const totalPetsChartOptions = {
    chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false } },
    stroke: { curve: "smooth", width: 2.5, dashArray: [0, 5] },
    colors: ["#1976D2", "#10B981"],
    xaxis: {
      categories: trendCategories,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "12px", colors: "#616161" } },
    },
    yaxis: {
      min: 0, max: 70, tickAmount: 7,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "12px", colors: "#616161" } },
    },
    legend: { position: "top", horizontalAlign: "left", fontFamily: "Montserrat, sans-serif", fontSize: "12px", markers: { width: 10, height: 10, radius: 2 } },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
  };

  const totalPetsChartSeries = [
    { name: "This year", data: thisYearData },
    { name: "Last year", data: lastYearData },
  ];

  // 3. Species Donut Chart Parsing
  const speciesArr = Array.isArray(overviewData?.species)
    ? overviewData.species
    : Array.isArray(overviewData?.speciesSplit)
    ? overviewData.speciesSplit
    : null;

  let speciesLabels = ["Dog", "Cat"];
  let speciesSeries = [80, 20];

  if (speciesArr && speciesArr.length > 0) {
    const valid = speciesArr.filter((s) => s && (s.percentage !== undefined || s.count !== undefined));
    if (valid.length > 0) {
      speciesLabels = valid.map((s) => String(s.label || s.species || s.name || ""));
      speciesSeries = valid.map((s) => Number(s.percentage ?? s.count ?? 0));
    }
  }

  const speciesChartOptions = {
    chart: {
      type: "donut", toolbar: { show: false },
      events: {
        dataPointSelection: (event, chartContext, config) => {
          const selectedIndex = config?.dataPointIndex;
          if (selectedIndex >= 0 && speciesLabels[selectedIndex]) {
            const spec = speciesLabels[selectedIndex];
            setSearchValues((prev) => ({ ...prev, petType: [{ name: spec, value: spec }] }));
          }
        },
      },
    },
    labels: speciesLabels,
    colors: ["#003366", "#64748B", "#CBD5E1"],
    plotOptions: { pie: { donut: { size: "60%" } } },
    dataLabels: { enabled: false },
    legend: { position: "bottom", horizontalAlign: "center", fontFamily: "Montserrat, sans-serif", fontSize: "12px", markers: { width: 10, height: 10, radius: 2 } },
    tooltip: { y: { formatter: (val) => `${val}%` } },
  };

  const speciesChartSeries = speciesSeries.map((v) => (typeof v === "number" && !isNaN(v) ? v : 0));

  // 4. Top Breeds Donut Chart Parsing
  const breedsArr = Array.isArray(overviewData?.topBreeds)
    ? overviewData.topBreeds
    : Array.isArray(overviewData?.breeds)
    ? overviewData.breeds
    : null;

  let breedsLabels = ["Akita", "Aidi", "Akbash", "Labrador", "Boxer"];
  let breedsSeries = [35, 25, 15, 15, 10];

  if (breedsArr && breedsArr.length > 0) {
    const valid = breedsArr.filter((b) => b && (b.percentage !== undefined || b.count !== undefined));
    if (valid.length > 0) {
      breedsLabels = valid.map((b) => String(b.breed || b.name || b.label || "Other"));
      breedsSeries = valid.map((b) => Number(b.percentage ?? b.count ?? 0));
    }
  }

  const topBreedsChartOptions = {
    chart: { type: "donut", toolbar: { show: false } },
    labels: breedsLabels,
    colors: ["#003366", "#2563EB", "#93C5FD", "#F59E0B", "#D97706"],
    plotOptions: { pie: { donut: { size: "60%" } } },
    dataLabels: { enabled: false },
    legend: { position: "bottom", horizontalAlign: "center", fontFamily: "Montserrat, sans-serif", fontSize: "12px", markers: { width: 10, height: 10, radius: 2 } },
  };

  const topBreedsChartSeries = breedsSeries.map((v) => (typeof v === "number" && !isNaN(v) ? v : 0));

  // 5. Age Distribution Stacked Bar Chart Parsing
  const ageArr = Array.isArray(overviewData?.ageDistributionByGender)
    ? overviewData.ageDistributionByGender
    : Array.isArray(overviewData?.ageDistribution)
    ? overviewData.ageDistribution
    : null;

  let ageCategories = ["0-1", "1-3", "3-6", "6-9", "9-15", "15+", "Unknown"];
  let maleAgeSeries = [16, 27, 45, 12, 12, 1, 0];
  let femaleAgeSeries = [9, 21, 21, 6, 9, 1, 0];
  let unknownAgeSeries = [22, 15, 20, 0, 0, 0, 18];

  if (ageArr && ageArr.length > 0) {
    ageCategories = ageArr.map((item) => String(item.ageGroup || item.category || ""));
    maleAgeSeries = ageArr.map((item) => Number(item.male ?? 0));
    femaleAgeSeries = ageArr.map((item) => Number(item.female ?? 0));
    unknownAgeSeries = ageArr.map((item) => Number(item.unknown ?? 0));
  }

  const ageDistributionChartOptions = {
    chart: { type: "bar", stacked: true, toolbar: { show: false } },
    plotOptions: { bar: { horizontal: false, columnWidth: "40%", borderRadius: 2 } },
    colors: ["#1976D2", "#F97316", "#CBD5E1"],
    xaxis: {
      categories: ageCategories,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "12px", colors: "#616161" } },
    },
    yaxis: {
      min: 0, max: 90, tickAmount: 9,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "12px", colors: "#616161" } },
    },
    legend: { position: "top", horizontalAlign: "left", fontFamily: "Montserrat, sans-serif", fontSize: "12px", markers: { width: 10, height: 10, radius: 2 } },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
  };

  const ageDistributionChartSeries = [
    { name: "Male", data: maleAgeSeries.map((v) => (typeof v === "number" && !isNaN(v) ? v : 0)) },
    { name: "Female", data: femaleAgeSeries.map((v) => (typeof v === "number" && !isNaN(v) ? v : 0)) },
    { name: "Unknown", data: unknownAgeSeries.map((v) => (typeof v === "number" && !isNaN(v) ? v : 0)) },
  ];

  const handleExportExcel = () => {
    const tableData = pets?.data ?? [];
    const exportData = tableData.map((row) => ({
      "Pet Name": row?.petName ?? "",
      "Pet Type": row?.petType ?? "",
      "Ownership Type": row?.ownershipType ?? "",
      "Pet Status": row?.petStatus ?? "",
      Species: row?.species ?? "",
      Gender: row?.gender ?? "",
      Breed: row?.breed ?? "",
      Color: row?.color ?? "",
      "Reproductive Status": row?.reproductiveStatus ?? "",
      "Blood Group": row?.bloodGroup ?? "",
      Temperament: row?.temperament ?? "",
      "Last Visit Date": row?.lastVisitDate ?? "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Pet Overview");
    XLSX.writeFile(workbook, `Pet_Overview_Report_${moment().format("YYYYMMDD")}.xlsx`);
  };

  const handleExportPDF = () => {
    window.print();
  };

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const reqObj = { type: "pets", page: currentPage, itemSize: rowsPerPage };

    if (dateRange) {
      const parts = dateRange.split(" to ");
      if (parts.length === 2) {
        reqObj.startDate = moment(parts[0], ["YYYY-MM", "YYYY-MM-DD"]).startOf("month").format("YYYY-MM-DD");
        reqObj.endDate = moment(parts[1], ["YYYY-MM", "YYYY-MM-DD"]).endOf("month").format("YYYY-MM-DD");
      }
    }

    if (selectedPetType && selectedPetType !== "all") {
      reqObj.petType = selectedPetType;
    }

    if (reset) return reqObj;
    if (searchValues?.petName?.length > 0) reqObj.petName = [searchValues?.petName];
    if (searchValues?.petType?.length > 0) reqObj.species = searchValues?.petType?.map((li) => li?.value);
    if (searchValues?.ownershipType?.length > 0) reqObj.ownershipType = searchValues?.ownershipType?.map((li) => li?.value);
    if (searchValues?.petStatus?.length > 0) reqObj.petStatus = searchValues?.petStatus?.map((li) => li?.value);
    if (filterValues?.selectedList?.includes("Gender")) reqObj.gender = filterValues?.values?.["Gender"]?.map((li) => li?.value);
    if (filterValues?.selectedList?.includes("Breed")) reqObj.breed = filterValues?.values?.["Breed"]?.map((li) => li?.value);
    if (filterValues?.selectedList?.includes("Color")) reqObj.color = [filterValues?.values?.["Color"] ?? ""];
    if (filterValues?.selectedList?.includes("Reproductive Status")) reqObj.reproductiveStatus = filterValues?.values?.["Reproductive Status"]?.map((li) => li?.value);
    if (filterValues?.selectedList?.includes("Blood Group")) reqObj.bloodGroup = filterValues?.values?.["Blood Group"]?.map((li) => li?.value);
    if (filterValues?.selectedList?.includes("Temperament")) reqObj.temperament = filterValues?.values?.["Temperament"]?.map((li) => li?.value);
    if (filterValues?.selectedList?.includes("Last Visit Date")) {
      reqObj.startDate = moment(new Date(filterValues?.values?.["Start Date"] ?? new Date())).format("YYYY-MM-DD");
      reqObj.endDate = moment(new Date(filterValues?.values?.["End Date"] ?? new Date())).format("YYYY-MM-DD");
    }
    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    dispatch(getPetsReportData(getFilterObj(pge, reset)));
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find((tblHdr) => tblHdr?.name === pd);
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => setEditColModVsble(false);

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders?.filter((act) => act?.isSelected)?.map((act) => act?.name);
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const totalPage = Math.ceil((pets?.totalRecords ?? 0) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => onApiCall(selectedPage);

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({ ...searchValues, [name?.trim()?.replace(/,$/, "")]: value });
  };

  const onFilterChange = (name, value) => setFilterValues({ ...filterValues, [name]: value });

  const onReset = () => {
    flushSync(() => setSearchValues(initSearch));
    flushSync(() => setFilterValues({ ...initFilter, selectedList: [], values: {} }));
    onApiCall(1, true);
  };

  const getPetList = () => uniq((allPets?.data ?? [])?.map((li) => li?.petName)?.filter(Boolean));

  const getPetTypes = () => {
    const reqList = (allPets?.data ?? [])?.map((li) => li?.petType?.toLowerCase())?.filter(Boolean);
    return uniq(reqList)?.map((li) => ({ name: capitalize(li), value: li }));
  };

  const onFilterReset = () => setFilterValues({ ...initFilter, visible: true, selectedList: [], values: {} });

  const onApplyFilter = () => {
    onApiCall();
    setFilterValues({ ...filterValues, visible: false });
  };

  const onCloseFilter = () => setFilterValues({ ...filterValues, visible: false });

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    setFilterValues({ ...filterValues, values: { ...filterValues?.values, [name?.trim()?.replace(/,$/, "")]: value } });
  };

  const onFilterAdd = (value) => setFilterValues({ ...filterValues, added: value });

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) reqList = reqList?.filter((item) => item !== name);
    else reqList.push(name);
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const getSpeciesList = () => uniq((allPets?.data ?? [])?.map((li) => li?.species)?.filter(Boolean))?.map((li) => ({ name: li, value: li }));
  const getBreedList = () => uniq((allPets?.data ?? [])?.map((li) => li?.breed)?.filter(Boolean))?.map((li) => ({ name: li, value: li }));
  const getColorList = () => uniq((allPets?.data ?? [])?.map((li) => li?.color)?.filter(Boolean));

  const getDropListByType = (typ) => {
    return typ === "Species" ? getSpeciesList() : typ === "Gender" ? genderList : typ === "Breed" ? getBreedList() : typ === "Color" ? getColorList() : typ === "Temperament" ? temperaments : typ === "Blood Group" ? bloodGroups : typ === "Reproductive Status" ? NeuteredList : [];
  };

  return (
    <div className="pet-overview-container">
      {/* Heading */}
      <h2 className="pet-overview-title">Pet overview</h2>

      {/* Top Filter Bar */}
      <div className="pet-overview-filter-bar">
        <Select
          size="small"
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          sx={{
            height: 36,
            fontSize: 13,
            fontFamily: "Montserrat, sans-serif",
            borderRadius: "6px",
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
          }}
        >
          <MenuItem value="2025-08 to 2026-08">2025-08 to 2026-08</MenuItem>
          <MenuItem value="2026-01 to 2026-08">2026-01 to 2026-08</MenuItem>
        </Select>

        <Select
          size="small"
          value={selectedPetType}
          onChange={(e) => setSelectedPetType(e.target.value)}
          sx={{
            height: 36,
            fontSize: 13,
            fontFamily: "Montserrat, sans-serif",
            borderRadius: "6px",
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
          }}
        >
          <MenuItem value="all">All pet types</MenuItem>
          <MenuItem value="dog">Dog</MenuItem>
          <MenuItem value="cat">Cat</MenuItem>
        </Select>
      </div>

      {/* Export Action Buttons */}
      <div className="pet-overview-export-group">
        <button
          type="button"
          className="pet-overview-export-btn"
          onClick={handleExportExcel}
        >
          <DownloadIcon sx={{ fontSize: 16 }} /> Export Excel
        </button>
        <button
          type="button"
          className="pet-overview-export-btn"
          onClick={handleExportPDF}
        >
          <DownloadIcon sx={{ fontSize: 16 }} /> Export PDF
        </button>
      </div>

      {/* Summary Cards Grid (5 Cards) */}
      <div className="pet-overview-cards-grid">
        {summaryCards.map((card, idx) => (
          <div className="pet-overview-card" key={idx}>
            <h4 className="pet-overview-card-title">{card.title}</h4>
            <div className="pet-overview-card-value-container">
              <h2 className="pet-overview-card-value">{card.value}</h2>
            </div>
          </div>
        ))}
      </div>

      {/* Chart 1: Total pets, this year vs last year */}
      <div className="pet-overview-chart-card">
        <div className="pet-overview-chart-header">
          Total pets, this year vs last year
        </div>
        <Chart
          options={totalPetsChartOptions}
          series={totalPetsChartSeries}
          type="line"
          height={300}
        />
      </div>

      {/* Charts Section 2: Species & Top Breeds (2 Donut Charts) */}
      <div className="pet-overview-charts-grid">
        <div className="pet-overview-chart-card">
          <div className="pet-overview-chart-header">
            Species (click to filter table)
          </div>
          <Chart
            options={speciesChartOptions}
            series={speciesChartSeries}
            type="donut"
            height={280}
          />
        </div>

        <div className="pet-overview-chart-card">
          <div className="pet-overview-chart-header">Top breeds</div>
          <Chart
            options={topBreedsChartOptions}
            series={topBreedsChartSeries}
            type="donut"
            height={280}
          />
        </div>
      </div>

      {/* Chart 3: Age distribution by gender */}
      <div className="pet-overview-chart-card">
        <div className="pet-overview-chart-header">
          Age distribution by gender
        </div>
        <Chart
          options={ageDistributionChartOptions}
          series={ageDistributionChartSeries}
          type="bar"
          height={320}
        />
      </div>

      {/* Drill-down Header */}
      <div className="pet-overview-drilldown-header">Drill-down — roster</div>

      {/* Search Filters Row */}
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              options={getPetList()}
              value={searchValues?.petName ?? []}
              name="petName"
              handleChange={onSearchChange}
              label="Pet Name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
              suggetions
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={getPetTypes()}
              value={searchValues?.petType ?? []}
              name="petType"
              handleChange={onSearchChange}
              label="Pet Type"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={petOwnershipTypesForDrop}
              value={searchValues?.ownershipType ?? []}
              name="ownershipType"
              handleChange={onSearchChange}
              label="Ownership Type"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={reportPetTypes}
              value={searchValues?.petStatus ?? []}
              name="petStatus"
              handleChange={onSearchChange}
              label="Pet Status"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div
            className="flex-row-ali-cen ml20 filter cursor"
            onClick={() => onFilterChange("visible", true)}
          >
            <TuneIcon sx={{ color: AppColors.appPrimary }} />
            <Typography className="font-medium fs12 blue-color ml10">
              {filterValues?.selectedList?.length > 0
                ? `${filterValues?.selectedList?.length} `
                : ""}
              Filter
            </Typography>
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleEditColumn}
          >
            <EditOutlinedIcon sx={w20h20BlueIcon} />
            <Typography className="blue-color fs16 font-medium ml5">
              Edit columns ({crntTblHeders?.length})
            </Typography>
          </div>
        </div>
      </div>

      <Typography className="font-medium fs12 gray28 ml30">
        Number of Records: {pets?.totalRecords ?? 0}
      </Typography>

      <div className="mtMin10">
        <ReportTable
          datas={pets?.data ?? []}
          columns={crntTblHeders}
          maxHeight="none"
        />
        {Math.ceil((pets?.totalRecords ?? 0) / rowsPerPage) > 1 ? (
          <div className="flex1-end mt20 mb30">
            <Pagination
              count={totalPage}
              variant="outlined"
              color="primary"
              page={page}
              onChange={handleChangePage}
            />
          </div>
        ) : null}
      </div>

      {/* Edit Columns Modal */}
      <CustomModal
        open={editColModVsble}
        onClose={onEditModClose}
        header="Columns"
        modal
        modalWidth={25}
      >
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <CustomButton
              text="Save"
              smallBtn
              onClick={handleSaveTableColumn}
            />
          </div>
        </div>
      </CustomModal>

      {/* Filter Modal */}
      <CustomModal
        open={filterValues?.visible}
        onClose={onCloseFilter}
        header="Filter"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className="gray38 fs12 txt-regular">
                Filter by:
              </Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <CustomCheckbox
                      checked={filterValues?.selectedList?.includes(li)}
                      onChange={() => onSelectFilter(li)}
                    />
                    <Typography className="txt-regular fs14 black mb5 mlMin15">
                      {li}
                    </Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div
                    className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor"
                    onClick={() => onFilterAdd(false)}
                  >
                    <AddIcon
                      sx={{ color: AppColors.appPrimary, fontSize: 15 }}
                    />
                    <Typography className="font-medium fs12 blue-color ml5">
                      Add Filter
                    </Typography>
                  </div>
                  {filterValues?.selectedList?.map((li) => (
                    <div className="mt20" key={li}>
                      {dropFields?.includes(li) ? (
                        <CustomSelect
                          list={getDropListByType(li)}
                          value={filterValues?.values?.[li] ?? []}
                          name={li}
                          handleChange={onFilterValuesChange}
                          label={li}
                          multiSelectSearchCheck
                        />
                      ) : li === "Last Visit Date" ? (
                        <div className="flex-row-between-align-center">
                          <div className="w45Per">
                            <CustomTextField
                              placeholder="Start Date"
                              fullWidth
                              name="Start Date"
                              handleChange={onFilterValuesChange}
                              value={
                                filterValues?.values?.["Start Date"] ?? ""
                              }
                              label="Start Date"
                              mobileDate
                            />
                          </div>
                          <div className="w45Per">
                            <CustomTextField
                              placeholder="End Date"
                              fullWidth
                              name="End Date"
                              handleChange={onFilterValuesChange}
                              value={
                                filterValues?.values?.["End Date"] ?? ""
                              }
                              label="End Date"
                              mobileDate
                            />
                          </div>
                        </div>
                      ) : (
                        <CustomTextField
                          placeholder={li}
                          fullWidth
                          name={li}
                          handleChange={onFilterValuesChange}
                          value={filterValues?.values?.[li] ?? ""}
                          label={li}
                          suggetions
                          options={getSpeciesList(li)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <CustomButton
                    text="Add"
                    smallBtn
                    onClick={() => onFilterAdd(true)}
                  />
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <CustomButton
                    text="Reset All"
                    smallBtn
                    onClick={onFilterReset}
                    tabSelectdBtn
                  />
                  <div className="ml20">
                    <CustomButton
                      text="Apply Filters"
                      smallBtn
                      onClick={onApplyFilter}
                    />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </div>
  );
};

export default PetDetailsReport;
